import { showToast, ToastrHelper } from '$lib/helpers/toastrHelper';
import { fetchWithAuth } from '$lib/utils/http/httpUtil';
import { extractErrorMessage } from '$lib/utils/error/errorUtils';
import { type ErrorResponse } from '$lib/models/types';
import {
    type GetClientDeleteSummaryResponse,
    type GetClientDeleteDetailsResponse,
    type ClientDeleteResponse,
    type DeleteClientInformationResponse
} from '$lib/models/deleteclient/types';

function createDeleteClientStore() {
    const type: string = 'DeleteClient';
    const baseUrl: string = '/api/delete-client';

    return {
        // This fetches the current delete information for a client
        fetchDeleteInformation: async (id: number): Promise<DeleteClientInformationResponse | ErrorResponse> => {
            const response = await fetchWithAuth(`${baseUrl}/${id}/delete-information`, {
                method: 'GET',
            });

            if (response.status == 204) {
                const errorMessage = ToastrHelper.notFound(type);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                    detail: errorMessage, // Add detail field for Problem Details compatibility
                };
            }

            if (!response.ok) {
                const errorMessage = await extractErrorMessage(response, `Failed to fetch ${type} (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                    detail: errorMessage, // Add detail field for Problem Details compatibility
                };
            }

            try {
                return await response.json();
            } catch (error) {
                const errorMessage = ToastrHelper.errorRetrieving(type);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                };
            }
        },

        // These are for the client data store
        fetchDeleteSummary: async (id: number): Promise<GetClientDeleteSummaryResponse | ErrorResponse> => {
            const response = await fetchWithAuth(`${baseUrl}/${id}/delete-summary`, {
                method: 'GET',
            });

            if (response.status == 204) {
                const errorMessage = ToastrHelper.notFound(type);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                    detail: errorMessage, // Add detail field for Problem Details compatibility
                };
            }

            if (!response.ok) {
                const errorMessage = await extractErrorMessage(response, `Failed to fetch ${type} (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                    detail: errorMessage, // Add detail field for Problem Details compatibility
                };
            }

            try {
                return await response.json();
            } catch (error) {
                const errorMessage = ToastrHelper.errorRetrieving(type);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                };
            }
        },
        fetchDeleteDetails: async (id: number): Promise<GetClientDeleteDetailsResponse | ErrorResponse> => {
            const response = await fetchWithAuth(`${baseUrl}/${id}/delete-details`, {
                method: 'GET',
            });

            if (response.status == 204) {
                const errorMessage = ToastrHelper.notFound(type);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                    detail: errorMessage, // Add detail field for Problem Details compatibility
                };
            }

            if (!response.ok) {
                const errorMessage = await extractErrorMessage(response, `Failed to fetch ${type} (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                    detail: errorMessage, // Add detail field for Problem Details compatibility
                };
            }

            try {
                return await response.json();
            } catch (error) {
                const errorMessage = ToastrHelper.errorRetrieving(type);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                };
            }
        },
        deleteClient: async (id: number): Promise<ClientDeleteResponse | ErrorResponse> => {

            const response = await fetchWithAuth(`${baseUrl}/${id}/delete-client`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                return await response.json();
            } else {
                // Use the centralized error message extraction
                const errorMessage = await extractErrorMessage(response, `Failed to delete client (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                throw new Error(errorMessage);
            }
        },

        // These are for the legacy primary blob storage
        fetchLegacyBlobStorageDetails: async (id: number): Promise<GetClientDeleteDetailsResponse | ErrorResponse> => {
            const response = await fetchWithAuth(`${baseUrl}/${id}/legacy-blob-storage-details`, {
                method: 'GET',
            });

            if (response.status == 204) {
                const errorMessage = ToastrHelper.notFound(type);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                    detail: errorMessage, // Add detail field for Problem Details compatibility
                };
            }

            if (!response.ok) {
                const errorMessage = await extractErrorMessage(response, `Failed to fetch ${type} (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                    detail: errorMessage, // Add detail field for Problem Details compatibility
                };
            }

            try {
                return await response.json();
            } catch (error) {
                const errorMessage = ToastrHelper.errorRetrieving(type);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return {
                    message: errorMessage,
                };
            }
        },
        deleteLegacyBlobStorage: async (id: number): Promise<ClientDeleteResponse | ErrorResponse> => {

            const response = await fetchWithAuth(`${baseUrl}/${id}/delete-legacy-blob-storage`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                return await response.json();
            } else {
                // Use the centralized error message extraction
                const errorMessage = await extractErrorMessage(response, `Failed to delete legacy blob storage (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                throw new Error(errorMessage);
            }
        },

        // These are for items actions
        markItemAsDeleted: async (id: number, itemType: string): Promise<ClientDeleteResponse | ErrorResponse> => {

            const response = await fetchWithAuth(`${baseUrl}/${id}/${itemType}/mark-item-as-deleted`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                return await response.json();
            } else {
                // Use the centralized error message extraction
                const errorMessage = await extractErrorMessage(response, `Failed to mark item as completed (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                throw new Error(errorMessage);
            }
        },
    };
}

export const deleteClientStore = createDeleteClientStore();